import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Adventures', path: '/adventures' },
    { name: 'Typical Weekend', path: '/weekend' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'FAQ', path: '/faq' }
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled || !isHomePage
        ? 'bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-lg'
        : 'bg-white/5 backdrop-blur-md border-b border-white/10'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link 
            to="/" 
            className={`text-2xl font-bold transition-all duration-300 ${
              isScrolled || !isHomePage
                ? 'text-slate-800 hover:text-coral-500'
                : 'text-white drop-shadow-lg hover:text-coral-300'
            }`}
          >
            🌊 Dive Camp Adventures
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`transition-all duration-300 font-medium ${
                  location.pathname === item.path
                    ? isScrolled || !isHomePage
                      ? 'text-coral-500 font-semibold'
                      : 'text-coral-300 font-semibold'
                    : isScrolled || !isHomePage
                    ? 'text-slate-700 hover:text-coral-400'
                    : 'text-white drop-shadow-md hover:text-coral-300'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/book"
              className="bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 text-white px-6 py-2 rounded-full transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg ${
              isScrolled || !isHomePage
                ? 'text-slate-700 hover:bg-slate-100'
                : 'text-white hover:bg-white/20'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden pb-4"
          >
            <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-slate-200 p-4 mt-2">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-3 px-4 rounded-xl font-medium transition-all ${
                    location.pathname === item.path
                      ? 'bg-coral-500 text-white'
                      : 'text-slate-700 hover:bg-coral-50 hover:text-coral-600'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/book"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block mt-4 bg-gradient-to-r from-coral-500 to-coral-600 text-white py-3 px-4 rounded-xl font-semibold text-center"
              >
                Book Adventure
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;